/**
 * This is copied from `src/sections/profile/containers/navigation` but uses
 * the navigation API resource instead of redux to populate its data. Any changes made here
 * should be synced to that file as well, until all workspace pages are able to use the
 * react-resource-router and the navigation resource (then that file can be deleted).
 */
import { MenuItem } from '@atlassian/bitbucket-navigation';
import React from 'react';
import Loadable from 'react-loadable';
import { useResource } from 'react-resource-router';

import { useAppData } from 'src/app/data';
import { HiddenLoader } from 'src/components/react-loadable';
import { WorkspaceLink } from 'src/components/workspace-link';
import { BANNER_HEIGHT } from 'src/constants/navigation';
import Navigation from 'src/sections/global/containers/navigation-v2';
import { workspaceNavigationResource } from 'src/sections/workspace/workspace-navigation-resource';

import { useWorkspaceContainerNavigationState } from './use-container-navigation-props';
import { useConsentBanner } from 'src/app/navigation-provider';

const MAIN_NAV_MENU_ITEM_COUNT = 3;

type HorizontalNavigationItems = {
  mainItems: MenuItem[];
  secondaryItems: MenuItem[];
  settingsItems: MenuItem[];
};

const WorkspaceContainerNavigation = Loadable({
  loading: HiddenLoader,
  loader: () =>
    import(
      'src/sections/workspace/components/navigation/container-navigation',
      // @ts-ignore
      { preload: true }
    ),
});

const useWorkspaceNavigationState = () => {
  const { data, error, loading } = useResource(workspaceNavigationResource);

  return {
    data: {
      attributionsUrl: data?.links?.attributions?.href,
      manageAccountUrl: data?.links?.manage_account?.href,
      switchAccountUrl: data?.links?.switch_account?.href,
      userEmail: data?.user?.email,
      // Fall back to an empty string because some link components (e.g., RelativeLink) expect a string
      containerHref: data?.workspace.links.html.href || '',
      containerLogo: data?.workspace.links.avatar.href,
      containerName: data?.workspace.name,
      globalBitbucketActions: data?.menuItems.actions || [],
      horizontalNavigationItems: data
        ? {
            mainItems: data.menuItems.main,
            secondaryItems: data.menuItems.secondary,
            settingsItems: data.menuItems.settings,
          }
        : // Add fake/empty menu items in order to stub out enough space for the real ones to render
          // once the data is fetched, otherwise they collapse into a "..." menu
          // This seems like it could be a bug in `AtlassianNavigation` from `@atlaskit/atlassian-navigation`
          // when the value of the `primaryItems` prop changes (and the # of items in the array increases)
          // @ts-ignore
          ({
            mainItems: Array(MAIN_NAV_MENU_ITEM_COUNT).fill({}),
            secondaryItems: [],
            settingsItems: [],
          } as HorizontalNavigationItems),
      loginUrl: data?.links.login?.href,
      logoutUrl: data?.links.logout?.href,
      targetUserDisplayName: data?.workspace.name,
      targetUserUuid: data?.workspace.uuid,
      userAvatarUrl: data?.user?.links.avatar.href,
      userDisplayName: data?.user?.display_name,
      userUuid: data?.user?.uuid,
      userWorkspaceId: data?.user?.extra?.workspace_slug,
      whatsNewUrl: data?.links.whatsNew.href,
      currentUser: data?.user,
      currentWorkspace: data?.workspace,
    },
    error,
    loading,
  };
};

type Props = {
  isBannerOpen?: boolean;
  isBeingRenderedInsideMobileNav?: boolean;
  containerType?: string;
};

export const WorkspaceNavigation: React.FC<Props> = props => {
  const { isBannerOpen } = props;
  // TODO: Add dedicated error handling
  const { data: navigationState } = useWorkspaceNavigationState();
  const { features, navigationIsOpen } = useAppData();
  const { consentBannerHeight } = useConsentBanner()[0];
  const topOffset = isBannerOpen
    ? BANNER_HEIGHT + consentBannerHeight
    : consentBannerHeight;

  return (
    <Navigation
      navigationType="product"
      createDrawerHeaderLinkComponent={WorkspaceLink}
      // TODO: Move this prop down into `src/sections/global/containers/navigation-v2.tsx` once
      // `globalState` is safe to use across all pages
      defaultNavigationOpen={navigationIsOpen}
      // TODO: Move this prop down into `src/sections/global/containers/navigation-v2.tsx` once
      // `globalState` is safe to use across all pages
      hasDarkNavTheme={!!features['theme-dark-nav']}
      // TODO: Move this prop down into `src/sections/global/containers/navigation-v2.tsx` once
      // `globalState` is safe to use across all pages
      hasPride={!!features['bbc.core.pride-logo']}
      hasPlatformNotificationsEnabled={!!features['bbc-platform-notifications']}
      isGlobalContext={false}
      containerNavigationComponent={WorkspaceContainerNavigation}
      // TODO: Move this prop down into `src/sections/global/containers/navigation-v2.tsx` once
      // `globalState` is safe to use across all pages and we can calculate isBannerOpen there
      topOffset={topOffset}
      useContainerNavigationProps={useWorkspaceContainerNavigationState}
      {...navigationState}
      {...props}
      containerType={props.containerType || 'workspace'}
    />
  );
};
